import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { messageService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { PageContainer, PageHeader } from '@/components/layout'
import { MessageStatus } from '@/types/enums'
import { MessageForm, type MessageFormData } from '@/components/messages/MessageForm'
import type { User } from '@/types'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'

export default function MessageCreatePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
  const [secondaryActionLoading, setSecondaryActionLoading] = useState(false)
  const [recipientUser, setRecipientUser] = useState<User | null>(null)
  const [loadingRecipient, setLoadingRecipient] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  // Load recipient user if specified in query params
  useEffect(() => {
    const userId = searchParams.get('user')
    if (userId) {
      setLoadingRecipient(true)
      userService
        .getById(userId)
        .then((foundUser) => {
          if (foundUser) {
            setRecipientUser(foundUser)
          } else {
            toast.error('User not found')
          }
        })
        .catch((error) => {
          console.error('Failed to load user:', error)
          toast.error('Failed to load recipient user')
        })
        .finally(() => {
          setLoadingRecipient(false)
        })
    }
  }, [searchParams])

  const handleSubmit = async (data: MessageFormData, isDraft: boolean) => {
    if (!user) return

    setLoading(true)
    if (isDraft) {
      setSecondaryActionLoading(true)
    } else {
      setPrimaryActionLoading(true)
    }

    try {
      const status = isDraft ? MessageStatus.Draft : MessageStatus.Published
      const newMessage = await messageService.createMessage(data, user.id, status)

      if (isDraft) {
        toast.success('Message saved as draft')
      } else {
        toast.success('Message sent successfully')
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      // Navigate to the newly created message
      navigate(`/messages/${newMessage.id}`)
    } catch (error) {
      console.error('Failed to save message:', error)
      toast.error(isDraft ? 'Failed to save draft' : 'Failed to send message')
    } finally {
      setLoading(false)
      setPrimaryActionLoading(false)
      setSecondaryActionLoading(false)
    }
  }

  // Build initial data if recipient user is specified
  const initialData = recipientUser
    ? {
        directMessage: true,
        users: [recipientUser],
      }
    : undefined

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title={recipientUser ? `New Message to ${recipientUser.name}` : 'Create New Message'}
      />

      {loadingRecipient ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            <p className="text-sm text-muted-foreground">Loading recipient...</p>
          </div>
        </div>
      ) : (
        <MessageForm
          mode="create"
          initialData={initialData}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/messages')}
          loading={loading}
          primaryActionLoading={primaryActionLoading}
          secondaryActionLoading={secondaryActionLoading}
          onDirtyChange={setIsDirty}
        />
      )}

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
